import React from 'react';
import { useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Paper,
    Avatar,
    Chip,
    Grid,
    useTheme,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    AccountCircle as AccountIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
} from '@mui/icons-material';
import { RootState } from '../../types';

const UserProfile: React.FC = () => {
    const theme = useTheme();
    // Get current user from auth state
    const { user, isLoading, error } = useSelector((state: RootState) => state.auth);

    if (isLoading) {
        return (
            <Box sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                px: 3,
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress
                        sx={{
                            color: theme.palette.primary.main,
                            mb: 2,
                        }}
                    />
                    <Typography sx={{ color: theme.palette.text.secondary }}>
                        Loading profile...
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                px: 3,
            }}>
                <Alert severity="error" sx={{ maxWidth: 400 }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{
                backgroundColor: theme.palette.background.default,
                minHeight: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                px: 3,
            }}>
                <Alert severity="warning" sx={{ maxWidth: 400 }}>
                    Please log in to view your profile
                </Alert>
            </Box>
        );
    }

    const getRoleColor = (role: string) => {
        return role === 'global_admin' ? 'error' : 'primary';
    };

    return (
        <Box sx={{
            backgroundColor: theme.palette.background.default,
            minHeight: '100vh',
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            px: 3,
        }}>
            <Box sx={{
                maxWidth: '800px',
                width: '100%',
                py: 3,
            }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 4,
                }}>
                    <Box>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                            }}
                        >
                            User Profile
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                            }}
                        >
                            Manage your account information
                        </Typography>
                    </Box>
                </Box>

                {/* Profile Card */}
                <Paper sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.border.light}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                }}>
                    {/* Profile Header */}
                    <Box sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        p: 4,
                        textAlign: 'center',
                        position: 'relative',
                    }}>
                        <Avatar
                            sx={{
                                width: 80,
                                height: 80,
                                fontSize: '2rem',
                                fontWeight: 700,
                                margin: '0 auto',
                                mb: 2,
                                background: theme.palette.error.main,
                                color: theme.palette.grey["100"],
                                border: `4px solid ${theme.palette.background.paper}`,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                            }}
                        >
                            {user.first_name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Typography
                            variant="h5"
                            sx={{
                                color: theme.palette.primary.contrastText,
                                fontWeight: 700,
                                mb: 1,
                            }}
                        >
                            {user.first_name} {user.last_name}
                        </Typography>
                        <Chip
                            label={user.role === 'global_admin' ? 'Administrator' : user.role === 'client_user' ? 'Client' : 'Operator'}
                            color={getRoleColor(user.role) as any}
                            sx={{
                                fontWeight: 600,
                                borderRadius: 2,
                            }}
                        />
                    </Box>

                    {/* Profile Details */}
                    <Box sx={{ p: 4 }}>
                        <Grid container spacing={3}>
                            {/* Personal Information */}
                            <Grid size = {{xs: 12}}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <PersonIcon sx={{ color: theme.palette.primary.main }} />
                                    Personal Information
                                </Typography>
                                <Divider sx={{ mb: 3, borderColor: theme.palette.border.light }} />
                            </Grid>

                            {/* First Name */}
                            <Grid size = {{xs: 12, sm:6}}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: theme.palette.background.secondary,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.border.light}`,
                                }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            mb: 0.5,
                                        }}
                                    >
                                        First Name
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {user.first_name}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Last Name */}
                            <Grid size = {{xs: 12, sm:6}}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: theme.palette.background.secondary,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.border.light}`,
                                }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            mb: 0.5,
                                        }}
                                    >
                                        Last Name
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {user.last_name}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Account Information */}
                            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 700,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <AccountIcon sx={{ color: theme.palette.primary.main }} />
                                    Account Information
                                </Typography>
                                <Divider sx={{ mb: 3, borderColor: theme.palette.border.light }} />
                            </Grid>

                            {/* Email */}
                            <Grid size = {{xs: 12, sm: 2}}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: theme.palette.background.secondary,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.border.light}`,
                                }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            mb: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <EmailIcon sx={{ fontSize: '1rem' }} />
                                        Email Address
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Username */}
                            <Grid size = {{xs: 12, sm: 2}}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: theme.palette.background.secondary,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.border.light}`,
                                }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            mb: 0.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <BadgeIcon sx={{ fontSize: '1rem' }} />
                                        Username
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {user.username}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Account Status */}
                            <Grid size = {{xs: 12, sm: 2}}>
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: theme.palette.background.secondary,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.border.light}`,
                                }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontWeight: 500,
                                            mb: 0.5,
                                        }}
                                    >
                                        Account Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {user.is_active ? (
                                            <ActiveIcon sx={{ color: theme.palette.success.main, fontSize: '1.2rem' }} />
                                        ) : (
                                            <InactiveIcon sx={{ color: theme.palette.error.main, fontSize: '1.2rem' }} />
                                        )}
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: user.is_active ? theme.palette.success.main : theme.palette.error.main,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {user.is_active ? 'Active' : 'Inactive'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default UserProfile;